import React, { useCallback, useState } from 'react';
import { IconButton } from '@lifeomic/chroma-react/components/IconButton';
import { Edit } from '@lifeomic/chromicons';
import { makeStyles } from '@lifeomic/chroma-react/styles';
import { EditTemplatingItemMetadataModal } from '../Modals/EditTemplatingItemMetadataModal';
import { TemplatingItem } from '../../models';
import { defineMessages, useIntl } from 'react-intl';
import { useTemplatingItemTypeDisplay } from '../../hooks/useTemplatingItemTypeDisplay';
import { clsx } from 'clsx';
import { useCanManageTemplates } from '../../hooks';

const messages = defineMessages({
  renameTemplatingItem: {
    id: 'RenameTemplatingItemButton.renameTemplatingItem',
    defaultMessage: 'Rename {typeName}',
  },
});

const useStyles = makeStyles(() => ({
  renameTemplateButton: {
    padding: 0,
  },
}));

type EditTemplatingItemMetadataButtonProps = {
  templatingItem: TemplatingItem;
  onTemplatingItemMetadataEdit?: () => void;
  className?: string;
};

export const EditTemplatingItemMetadataButton: React.FC<
  EditTemplatingItemMetadataButtonProps
> = ({ templatingItem, onTemplatingItemMetadataEdit, className }) => {
  const styles = useStyles();
  const [isEditingTemplatingItemMetadata, setIsEditingTemplatingItemMetadata] =
    useState(false);
  const { formatMessage } = useIntl();
  const templatingItemTypeDisplay = useTemplatingItemTypeDisplay();
  const canManageTemplates = useCanManageTemplates();

  const handleSuccess = useCallback(() => {
    setIsEditingTemplatingItemMetadata(false);
    onTemplatingItemMetadataEdit?.();
  }, [onTemplatingItemMetadataEdit]);

  const handleDismiss = useCallback(() => {
    setIsEditingTemplatingItemMetadata(false);
  }, []);

  const openModal: React.MouseEventHandler<HTMLButtonElement> = useCallback(
    event => {
      event.stopPropagation();
      event.preventDefault();
      setIsEditingTemplatingItemMetadata(true);
    },
    [],
  );

  if (!canManageTemplates) {
    return null;
  }

  return (
    <>
      <IconButton
        size={0}
        icon={Edit}
        aria-label={formatMessage(messages.renameTemplatingItem, {
          typeName: templatingItemTypeDisplay[templatingItem.type],
        })}
        onClick={openModal}
        className={clsx(className, styles.renameTemplateButton)}
        color="inverse"
      />
      <EditTemplatingItemMetadataModal
        templatingItem={templatingItem}
        isOpen={isEditingTemplatingItemMetadata}
        onSuccess={handleSuccess}
        onDismiss={handleDismiss}
      />
    </>
  );
};
